import { useEffect, useState } from "react";
import { CalculationBasis, CashbackType, CashbackTypeV2 } from "../../http/protocol";
import { getContainer } from "../../ioc/IOCSetup";
import { t } from "../../i18n";

export interface FractionLimits {
  rtp: number;
  unweighted: number;
  // Hard ceiling above the advisory warning band, applied regardless of calculation basis.
  blocking: number;
}

// The advisory "large fraction" warning limits are configured per operator in Admin Security
// (see SecuritySettingsView) and delivered to every admin via /admin-security/ui-settings. They are
// deliberately not hard-coded in the client: until they load, `thresholds` is undefined and no
// warning is shown.
//
// Rakeback and cashback carry separate limit sets because their fraction economics differ by an
// order of magnitude. The flat rtp/unweighted/blocking fields are the cashback (default) set; the
// optional `rakeback` set is selected when a rakeback reward is being edited. Keeping the default set
// flat lets a caller that has no reward type in hand read it directly, which is the more permissive
// choice and never over-blocks.
export interface CashbackFractionThresholds extends FractionLimits {
  rakeback?: FractionLimits;
}

type FractionGroup = "rakeback" | "cashback";

// An unknown reward type resolves to cashback — the more permissive set — so a missing type never
// blocks a save that a genuine cashback configuration would be allowed to make. Both enums share the
// same string values, so the comparison holds whichever one the caller passes.
function resolveGroup(type: CashbackType | CashbackTypeV2 | undefined): FractionGroup {
  return String(type) === CashbackType.RAKEBACK ? "rakeback" : "cashback";
}

// The reward type picks the set before the basis is consulted. Cashback (and anything unknown) reads
// the flat default set; rakeback reads its own set when one has loaded.
function limitsFor(thresholds: CashbackFractionThresholds, type: CashbackType | CashbackTypeV2 | undefined): FractionLimits {
  return resolveGroup(type) === "rakeback" && thresholds.rakeback ? thresholds.rakeback : thresholds;
}

// RTP_BASED multiplies only the small house-edge slice of a wager, so it tolerates a far larger
// fraction than UNWEIGHTED, which multiplies the whole wager — a single limit would misjudge one of
// the two bases.
export function largeFractionThreshold(basis: CalculationBasis, thresholds: CashbackFractionThresholds, type?: CashbackType | CashbackTypeV2): number {
  const limits = limitsFor(thresholds, type);
  return basis === CalculationBasis.RTP_BASED ? limits.rtp : limits.unweighted;
}

// Inclusive by design: a fraction that reaches the limit is already large enough to warn about. A
// blank or non-numeric entry parses to a non-finite value and never warns. Before the configured
// limits have loaded (or if the fetch failed) nothing is flagged.
export function isLargeFraction(
  fraction: number,
  basis: CalculationBasis,
  thresholds: CashbackFractionThresholds | undefined,
  type?: CashbackType | CashbackTypeV2,
): boolean {
  return !!thresholds && Number.isFinite(fraction) && fraction >= largeFractionThreshold(basis, thresholds, type);
}

// The hard stop that sits above the advisory band. Strict `>` (not the warning's inclusive `>=`): a
// fraction equal to the ceiling is still allowed, only a value beyond it is refused. Single limit for
// both bases (unlike the warning pair) because the ceiling guards against gross mistakes, not the
// basis-dependent economics the warning captures. Non-finite input (blank/garbled field) never blocks.
export function exceedsBlockingFraction(fraction: number, thresholds: CashbackFractionThresholds | undefined, type?: CashbackType | CashbackTypeV2): boolean {
  return !!thresholds && Number.isFinite(fraction) && fraction > limitsFor(thresholds, type).blocking;
}

// The ceiling blocks a save only when a fraction was BOTH changed this session AND is over the
// ceiling — a stored over-ceiling value left untouched is grandfathered. "Changed" is a raw-string
// compare of the live value against its loaded baseline, matching how the form fields hold their
// text; a row with no baseline (added this session, `initial` undefined) always reads as changed.
export function isNewlyBlockingFraction(
  current: number | string | undefined,
  initial: number | string | undefined,
  thresholds: CashbackFractionThresholds | undefined,
  type?: CashbackType | CashbackTypeV2,
): boolean {
  const changed = String(current ?? "") !== String(initial ?? "");
  return changed && exceedsBlockingFraction(Number(current), thresholds, type);
}

// The blocking ceiling for the reward type, surfaced so the "cannot save" copy can show the ceiling
// that actually stopped the save. Reward economics differ by an order of magnitude, so a rakeback
// block must quote the rakeback ceiling, not the flat cashback one. Undefined type resolves to the
// cashback set, matching the rest of the module; undefined thresholds (not yet loaded) yields undefined.
export function blockingFractionLimit(thresholds: CashbackFractionThresholds | undefined, type?: CashbackType | CashbackTypeV2): number | undefined {
  return thresholds && limitsFor(thresholds, type).blocking;
}

// Every cashback editor renders the identical "cannot save" copy when a fraction exceeds the ceiling;
// only the translation namespace differs (V1 `cashback.config`, V2/override `cashback.config-v2`), so
// the caller passes its own key. `max` is the configured ceiling for the message's {{max}} placeholder.
export function blockedFractionMessage(key: string, max: number | undefined): string {
  return t(key, { max }, "Cannot save: fraction exceeds the maximum allowed ({{max}}).");
}

// Loads the operator's configured warning limits once for a form. Returns undefined until they
// arrive (or if the fetch fails), which every caller treats as "no warning yet" rather than falling
// back to a value baked into the client.
export function useCashbackFractionThresholds(): CashbackFractionThresholds | undefined {
  const [thresholds, setThresholds] = useState<CashbackFractionThresholds | undefined>(undefined);
  useEffect(() => {
    let active = true;
    getContainer()
      .getSecuritySettingsService()
      .fetchUiSettings()
      .then((settings) => {
        if (active) {
          setThresholds({
            rtp: settings.largeCashbackFractionRtp,
            unweighted: settings.largeCashbackFractionUnweighted,
            blocking: settings.maxCashbackFraction,
            rakeback: {
              rtp: settings.largeRakebackFractionRtp,
              unweighted: settings.largeRakebackFractionUnweighted,
              blocking: settings.maxRakebackFraction,
            },
          });
        }
      })
      .catch(() => {
        // Advisory-only: on failure the forms simply show no large-fraction warning.
      });
    return () => {
      active = false;
    };
  }, []);
  return thresholds;
}
